import { InventoryItemConflictError } from "./stock";
import { applyResearchReview } from "./researchReview";
import { marketplaceModes, marketplaceLabels, marketplaces } from "./labels";
import {
  defaultMarketplaceProfile,
  marketplaceProfileDefinition
} from "./marketplaceCapabilities";
import { activeWorkspace, calculateNetProfit, currentUser, descriptionFromInput, generateSKU, listingDraftsForItem, previewListingDrafts } from "./logic";
import { money } from "./money";
import { purchaseCostIsKnown, resolvePurchaseCost, validateAcquiredDate } from "./inventoryFacts";
import { validateItemResearch } from "./research";
import { applyStockUpdate } from "./stock";
import { makeID } from "./seed";
import type {
  InventoryItem,
  InventoryItemInput,
  InventoryItemUpdate,
  ItemPhoto,
  ListingDraftUpdate,
  Marketplace,
  MarketplaceAccount,
  MarketplaceListing,
  PhotoInput,
  ReOSDatabase,
  Sale,
  SaleStatus,
  StoredAsset,
  Workspace
} from "./types";

const NO_PRODUCT_PHOTO_WARNING = "Add at least one product photo before preparing a listing draft.";
const LEGACY_NO_PRODUCT_PHOTO_WARNING = "Add at least one product photo before publishing.";
const LIVE_LISTING_URL_REQUIRED = "Paste the live listing URL before marking this listing live.";
const LIVE_LISTING_URL_INVALID = "Paste a full marketplace listing URL beginning with https://.";
const marketplaceListingHostnamePatterns: Partial<Record<Marketplace, RegExp>> = {
  ebay: /(^|\.)(?:ebay\.(?:at|be|ca|ch|co\.uk|com|com\.au|de|es|fr|ie|it|nl|pl)|ebay\.us)$/i,
  etsy: /(^|\.)(?:etsy\.com|etsy\.me)$/i,
  vinted: /(^|\.)vinted\.(?:at|be|co\.uk|com|cz|de|dk|es|fi|fr|gr|hr|hu|it|lt|lu|nl|pl|pt|ro|se|sk)$/i,
  depop: /(^|\.)depop\.com$/i
};
export const MAX_ITEM_PHOTOS = 24;
const internalListingStatusesClosedAfterSale = new Set(["draft", "ready", "failed"]);
const editableListingStatuses = new Set(["draft", "ready", "failed"]);
const closedListingStatuses = new Set(["sold", "ended", "delisted", "archived"]);
const itemConditions = new Set(["new", "new_with_tags", "new_without_tags", "like_new", "good", "fair", "poor", "unknown"]);
const inventoryItemUpdateKeys = [
  "researchReview",
  "stockAdjustment",
  "stockReviewAfter",
  "title",
  "description",
  "categoryID",
  "condition",
  "brand",
  "size",
  "color",
  "material",
  "sku",
  "stockLocation",
  "purchaseCostMinor",
  "purchaseCostStatus",
  "acquiredAt",
  "targetPriceMinor",
  "minimumPriceMinor",
  "quantity",
  "notes",
  "research"
] as const;
const listingDraftUpdateKeys = [
  "title",
  "description",
  "priceMinor",
  "categoryExternalID",
  "conditionExternalValue",
  "etsyWhoMade",
  "etsyWhenMade",
  "etsyIsSupply"
] as const;

export interface SaleInput {
  itemID: string;
  expectedItemUpdatedAt?: string;
  /** Optional per-unit cost, filled only when the inventory cost is unknown. */
  purchaseCostMinor?: number;
  listingID?: string;
  externalOrderID?: string;
  sourceNotificationID?: string;
  stockAlreadyUpdated?: boolean;
  marketplace: Marketplace;
  salePriceMinor: number;
  shippingChargedMinor: number;
  marketplaceFeesMinor: number;
  marketplaceFeesStatus?: "known" | "unknown";
  shippingCostStatus?: "known" | "unknown";
  processingFeesMinor: number;
  shippingCostMinor: number;
  packagingCostMinor: number;
  soldAt: string;
  buyerName: string;
  buyerAddress?: string;
  buyerPostcode: string;
  shipBy?: string;
  shippingCarrier?: string;
  shippingService?: string;
  trackingNumber?: string;
  fulfilmentNotes?: string;
  assigneeUserID?: string | null;
}

export interface SaleFulfilmentUpdate {
  shippingCostMinor?: number;
  status?: SaleStatus;
  buyerName?: string;
  buyerAddress?: string;
  buyerPostcode?: string;
  shipBy?: string;
  shippingCarrier?: string;
  shippingService?: string;
  trackingNumber?: string;
  fulfilmentNotes?: string;
  assigneeUserID?: string | null;
}

export interface WorkspaceSettingsUpdate {
  name?: string;
  defaultCurrency?: string;
  marketplaceSignalEmail?: string | null;
  marketplaceSignalsEnabledAt?: string | null;
  marketplaceProfiles?: Partial<Record<Marketplace, import("./types").MarketplaceConnectionProfile>>;
}

export const MARKETPLACE_SIGNAL_DOMAIN = "in.listwork.co";
const marketplaceSignalDomains = [MARKETPLACE_SIGNAL_DOMAIN, "staging.in.listwork.co"];
export const MARKETPLACE_SIGNAL_ADDRESS_PATTERN = new RegExp(
  `^events\\+[a-z0-9]{32}@(?:${marketplaceSignalDomains.map((domain) => domain.replace(/\./g, "\\.")).join("|")})$`
);

export function defaultMarketplaceAccounts(workspaceID: string, timestamp: string): MarketplaceAccount[] {
  return marketplaces.map((marketplace) => {
    const connectionProfile = defaultMarketplaceProfile(marketplace);
    const profile = marketplaceProfileDefinition(connectionProfile, marketplace);
    return {
      id: makeID(`account_${marketplace}`),
      workspaceID,
      marketplace,
      accountName: marketplaceLabels[marketplace],
      connectionStatus: "manual",
      integrationMode: marketplaceModes[marketplace],
      connectionProfile,
      capabilities: profile?.capabilities,
      createdAt: timestamp,
      updatedAt: timestamp
    };
  });
}

export function updateWorkspaceSettingsInDatabase(
  database: ReOSDatabase,
  workspaceID: string,
  update: WorkspaceSettingsUpdate,
  now = new Date()
): ReOSDatabase {
  const workspace = database.workspaces.find((candidate) => candidate.id === workspaceID);
  if (!workspace) {
    throw new Error("Workspace does not exist.");
  }

  const nextWorkspace = {
    ...workspaceWithSettings(workspace, update, now.toISOString()),
    marketplaceProfiles: update.marketplaceProfiles ?? workspace.marketplaceProfiles
  };
  return {
    ...database,
    workspaces: database.workspaces.map((candidate) => (candidate.id === workspaceID ? nextWorkspace : candidate))
  };
}

function assertWorkspaceSKUAvailable(database: ReOSDatabase, workspaceID: string, sku: string, exceptItemID?: string) {
  const normalized = sku.trim().toLocaleUpperCase("en-GB");
  const duplicate = database.inventoryItems.some((item) =>
    item.workspaceID === workspaceID &&
    item.id !== exceptItemID &&
    item.sku.trim().toLocaleUpperCase("en-GB") === normalized
  );
  if (duplicate) {
    throw new Error(`SKU ${sku.trim()} is already used by another inventory item.`);
  }
}

export function createInventoryWorkflowInDatabase(
  database: ReOSDatabase,
  input: InventoryItemInput,
  photos: PhotoInput[],
  selectedMarketplaces: Marketplace[]
): { database: ReOSDatabase; item: InventoryItem } {
  const workspace = activeWorkspace(database);
  const user = currentUser(database);
  if (!workspace || !user) {
    throw new Error("Create or load a workspace first.");
  }
  if (!input.title.trim()) {
    throw new Error("Item title is required.");
  }
  if (!input.categoryID.trim()) {
    throw new Error("Category is required.");
  }
  const purchaseCost = resolvePurchaseCost(input.purchaseCostMinor, input.purchaseCostStatus);
  if (input.targetPriceMinor < 0) {
    throw new Error("Target price cannot be negative.");
  }
  if (selectedMarketplaces.length > 0 && input.targetPriceMinor === 0) {
    throw new Error("Set a target price before creating marketplace drafts.");
  }
  if (photos.length > MAX_ITEM_PHOTOS) {
    throw new Error(`Each item can have up to ${MAX_ITEM_PHOTOS} photos.`);
  }
  for (const photo of photos) {
    const assetID = photo.assetID ?? photo.storedAsset?.id;
    const asset = photo.storedAsset ?? database.storedAssets.find(
      (candidate) => candidate.workspaceID === workspace.id && candidate.id === assetID
    );
    if (!assetID || !asset) {
      throw new Error("Photo asset does not exist in this workspace.");
    }
    if (asset.cleanupPendingAt) {
      throw new Error("Photo asset is pending deletion.");
    }
    if (asset.replacementForPhotoID) {
      throw new Error("Photo asset is reserved for another photo.");
    }
    if (asset.itemID) {
      throw new Error("Photo asset is already attached to another item.");
    }
  }

  const sku = input.sku.trim() || generateSKU(database, input.title, input.size);
  assertWorkspaceSKUAvailable(database, workspace.id, sku);
  const now = new Date().toISOString();
  const itemID = makeID("item");
  const item: InventoryItem = {
    id: itemID,
    workspaceID: workspace.id,
    title: input.title.trim(),
    description: descriptionFromInput(input, photos.length),
    categoryID: input.categoryID.trim(),
    condition: input.condition,
    brand: input.brand.trim() || undefined,
    size: input.size.trim() || undefined,
    color: input.color.trim() || undefined,
    material: input.material.trim() || undefined,
    sku,
    stockLocation: input.stockLocation.trim() || undefined,
    purchaseCost: money(input.purchaseCostMinor, workspace.defaultCurrency),
    purchaseCostStatus: purchaseCost.status,
    targetPrice: money(input.targetPriceMinor, workspace.defaultCurrency),
    minimumPrice: input.minimumPriceMinor === undefined ? undefined : money(input.minimumPriceMinor, workspace.defaultCurrency),
    quantity: Math.max(1, input.quantity),
    status: selectedMarketplaces.length > 0 ? "ready_to_list" : "draft",
    notes: input.notes.trim() || undefined,
    acquiredAt: input.acquiredAt ? validateAcquiredDate(input.acquiredAt) : undefined,
    createdAt: now,
    updatedAt: now
  };

  const itemPhotos: ItemPhoto[] = photos.map((photo, index) => ({
    id: makeID("photo"),
    itemID,
    assetID: photo.assetID ?? photo.storedAsset?.id,
    url: photo.url,
    thumbnailURL: photo.thumbnailURL,
    sortOrder: index,
    type: photo.type,
    width: photo.width,
    height: photo.height,
    treatment: photo.treatment,
    createdAt: now
  }));

  const accountMap = new Map(database.marketplaceAccounts.map((account) => [account.marketplace, account]));
  const drafts = previewListingDrafts(input, photos.length, selectedMarketplaces, database.marketplaceAccounts, workspace.defaultCurrency);
  const marketplaceListings: MarketplaceListing[] = drafts.map((draft) => {
    const account = accountMap.get(draft.marketplace) ?? defaultMarketplaceAccounts(workspace.id, now).find((candidate) => candidate.marketplace === draft.marketplace);
    if (!account) {
      throw new Error(`Missing marketplace account for ${draft.marketplace}.`);
    }
    return {
      id: makeID("listing"),
      workspaceID: workspace.id,
      itemID,
      marketplaceAccountID: account.id,
      marketplace: draft.marketplace,
      title: draft.title,
      description: draft.description,
      price: draft.price,
      shippingPrice: draft.shippingPrice,
      categoryExternalID: draft.categoryExternalID,
      conditionExternalValue: draft.conditionExternalValue,
      status: draft.validationWarnings.length > 0 ? "draft" : "ready",
      views: 0,
      watchers: 0,
      likes: 0,
      validationWarnings: draft.validationWarnings,
      createdAt: now,
      updatedAt: now
    };
  });

  const existingCategory = database.categories.some(
    (category) => category.workspaceID === workspace.id && category.path.toLowerCase() === item.categoryID.toLowerCase()
  );
  const categories = existingCategory
    ? database.categories
    : [
        ...database.categories,
        {
          id: makeID("category"),
          workspaceID: workspace.id,
          name: item.categoryID.split("/").pop()?.trim() ?? item.categoryID,
          path: item.categoryID,
          isSystem: false,
          createdAt: now,
          updatedAt: now
        }
      ];

  const uploadedAssets: StoredAsset[] = photos
    .map((photo) => photo.storedAsset)
    .filter((asset): asset is StoredAsset => Boolean(asset))
    .map((asset) => ({ ...asset, itemID, updatedAt: now }));
  const uploadedAssetIDs = new Set(uploadedAssets.map((asset) => asset.id));
  const linkedStoredAssets = database.storedAssets.map((asset) =>
    photos.some((photo) => (photo.assetID ?? photo.storedAsset?.id) === asset.id) && asset.workspaceID === workspace.id
      ? { ...asset, itemID, updatedAt: now }
      : asset
  );

  return {
    item,
    database: {
      ...database,
      inventoryItems: [...database.inventoryItems, item],
      itemPhotos: [...database.itemPhotos, ...itemPhotos],
      storedAssets: [...linkedStoredAssets.filter((asset) => !uploadedAssetIDs.has(asset.id)), ...uploadedAssets],
      marketplaceListings: [...database.marketplaceListings, ...marketplaceListings],
      categories,
      notifications: database.notifications
    }
  };
}

export function updateInventoryItemInDatabase(
  database: ReOSDatabase,
  itemID: string,
  update: InventoryItemUpdate,
  now = new Date()
): { database: ReOSDatabase; item: InventoryItem; replayed?: boolean } {
  const workspace = activeWorkspace(database);
  if (!workspace) {
    throw new Error("Create or load a workspace first.");
  }

  const item = database.inventoryItems.find((candidate) => candidate.workspaceID === workspace.id && candidate.id === itemID);
  if (!item) {
    throw new Error("Item does not exist in this workspace.");
  }
  if (!hasOwnUpdate(update, inventoryItemUpdateKeys)) {
    throw new Error("Item update is empty.");
  }

  if (update.researchReview) {
    if (Object.keys(update).some((key) => key !== "researchReview")) throw new Error("Save research changes separately.");
    update = applyResearchReview(item, update.researchReview, now, database.itemPhotos.filter((photo) => photo.itemID === item.id));
  } else if (update.expectedUpdatedAt && update.expectedUpdatedAt !== item.updatedAt) {
    throw new InventoryItemConflictError("This item changed. Refresh it and review the latest details.");
  }

  const title = requiredUpdatedText(update, "title", item.title, "Item title is required.");
  const description = requiredUpdatedText(update, "description", item.description, "Item description is required.");
  const categoryID = requiredUpdatedText(update, "categoryID", item.categoryID, "Category is required.");
  const sku = requiredUpdatedText(update, "sku", item.sku, "SKU is required.");
  assertWorkspaceSKUAvailable(database, workspace.id, sku, item.id);
  const condition = Object.prototype.hasOwnProperty.call(update, "condition") ? update.condition : item.condition;
  if (!condition || !itemConditions.has(condition)) {
    throw new Error("Choose a valid item condition.");
  }

  const costChanged = Object.prototype.hasOwnProperty.call(update, "purchaseCostMinor") || Object.prototype.hasOwnProperty.call(update, "purchaseCostStatus");
  const purchaseCost = costChanged
    ? resolvePurchaseCost(Object.prototype.hasOwnProperty.call(update, "purchaseCostMinor") ? update.purchaseCostMinor : update.purchaseCostStatus === "unknown" ? 0 : item.purchaseCost.amountMinor, update.purchaseCostStatus)
    : { amountMinor: item.purchaseCost.amountMinor, status: purchaseCostIsKnown(item) ? "known" as const : "unknown" as const };
  const targetPriceMinor = Object.prototype.hasOwnProperty.call(update, "targetPriceMinor")
    ? positiveMinorAmount(update.targetPriceMinor, "Target price")
    : item.targetPrice.amountMinor;
  const minimumPriceMinor = Object.prototype.hasOwnProperty.call(update, "minimumPriceMinor")
    ? update.minimumPriceMinor === null
      ? undefined
      : positiveMinorAmount(update.minimumPriceMinor, "Minimum price")
    : item.minimumPrice?.amountMinor;
  if (minimumPriceMinor !== undefined && minimumPriceMinor > targetPriceMinor) {
    throw new Error("Minimum price cannot exceed target price.");
  }

  const stockResult = applyStockUpdate(item, update, database.currentUserID ?? "", now);
  if (stockResult.replayed) return { database, item, replayed: true };
  const nextQuantity = Object.prototype.hasOwnProperty.call(update, "quantity") ? update.quantity : stockResult.item.quantity;
  if (!Number.isInteger(nextQuantity) || (nextQuantity ?? 0) < 0 || (Object.prototype.hasOwnProperty.call(update, "quantity") && nextQuantity === 0 && item.quantity !== 0)) {
    throw new Error("Quantity must be a whole number greater than zero.");
  }
  const quantity = nextQuantity as number;

  const timestamp = new Date(Math.max(now.getTime(), Date.parse(item.updatedAt) + 1)).toISOString();
  const updatedItem: InventoryItem = {
    ...stockResult.item,
    title,
    description,
    categoryID,
    condition,
    brand: optionalUpdatedText(update, "brand", item.brand),
    size: optionalUpdatedText(update, "size", item.size),
    color: optionalUpdatedText(update, "color", item.color),
    material: optionalUpdatedText(update, "material", item.material),
    sku,
    stockLocation: optionalUpdatedText(update, "stockLocation", item.stockLocation),
    purchaseCost: money(purchaseCost.amountMinor, item.purchaseCost.currency),
    purchaseCostStatus: purchaseCost.status,
    acquiredAt: Object.prototype.hasOwnProperty.call(update, "acquiredAt") ? validateAcquiredDate(update.acquiredAt, now) : item.acquiredAt,
    targetPrice: money(targetPriceMinor, item.targetPrice.currency),
    minimumPrice:
      minimumPriceMinor === undefined
        ? undefined
        : money(minimumPriceMinor, item.minimumPrice?.currency ?? item.targetPrice.currency),
    quantity,
    notes: optionalUpdatedText(update, "notes", item.notes),
    research: Object.prototype.hasOwnProperty.call(update, "research")
      ? update.research === null
        ? undefined
        : validateItemResearch(update.research as NonNullable<InventoryItemUpdate["research"]>, item.targetPrice.currency)
      : item.research,
    updatedAt: timestamp
  };

  const hasCategory = database.categories.some(
    (category) => category.workspaceID === workspace.id && category.path.toLowerCase() === categoryID.toLowerCase()
  );
  const categories = hasCategory
    ? database.categories
    : [
        ...database.categories,
        {
          id: makeID("category"),
          workspaceID: workspace.id,
          name: categoryID.split("/").pop()?.trim() ?? categoryID,
          path: categoryID,
          isSystem: false,
          createdAt: timestamp,
          updatedAt: timestamp
        }
      ];

  return {
    item: updatedItem,
    database: {
      ...database,
      inventoryItems: database.inventoryItems.map((candidate) => (candidate.id === updatedItem.id ? updatedItem : candidate)),
      categories
    }
  };
}

export function createListingDraftsForItemInDatabase(
  database: ReOSDatabase,
  itemID: string,
  selectedMarketplaces: Marketplace[],
  now = new Date()
): { database: ReOSDatabase; listings: MarketplaceListing[] } {
  const workspace = activeWorkspace(database);
  if (!workspace) {
    throw new Error("Create or load a workspace first.");
  }
  const item = database.inventoryItems.find((candidate) => candidate.workspaceID === workspace.id && candidate.id === itemID);
  if (!item) {
    throw new Error("Item does not exist in this workspace.");
  }
  if (item.status === "sold" || item.status === "archived") {
    throw new Error("Marketplace drafts cannot be created for sold or archived inventory.");
  }

  const requestedMarketplaces = [...new Set(selectedMarketplaces)];
  if (requestedMarketplaces.length === 0) {
    throw new Error("Choose at least one marketplace.");
  }
  const invalidMarketplace = requestedMarketplaces.find((marketplace) => !marketplaces.includes(marketplace));
  if (invalidMarketplace) {
    throw new Error("Choose a valid marketplace.");
  }
  const duplicateMarketplace = requestedMarketplaces.find((marketplace) =>
    database.marketplaceListings.some(
      (listing) =>
        listing.workspaceID === workspace.id &&
        listing.itemID === item.id &&
        listing.marketplace === marketplace &&
        !closedListingStatuses.has(listing.status)
    )
  );
  if (duplicateMarketplace) {
    throw new Error(`${marketplaceLabels[duplicateMarketplace]} already has an active listing for this item.`);
  }

  const timestamp = now.toISOString();
  const existingAccounts = database.marketplaceAccounts.filter((account) => account.workspaceID === workspace.id);
  const missingMarketplaces = requestedMarketplaces.filter(
    (marketplace) => !existingAccounts.some((account) => account.marketplace === marketplace)
  );
  const fallbackAccounts = defaultMarketplaceAccounts(workspace.id, timestamp).filter((account) => missingMarketplaces.includes(account.marketplace));
  const workspaceAccounts = [...existingAccounts, ...fallbackAccounts];
  const accountMap = new Map(workspaceAccounts.map((account) => [account.marketplace, account]));
  const photoCount = database.itemPhotos.filter((photo) => photo.itemID === item.id).length;
  const drafts = listingDraftsForItem(item, photoCount, requestedMarketplaces, workspaceAccounts);
  const listings = drafts.map((draft): MarketplaceListing => {
    const account = accountMap.get(draft.marketplace);
    if (!account) {
      throw new Error(`Missing marketplace account for ${draft.marketplace}.`);
    }
    return {
      id: makeID("listing"),
      workspaceID: workspace.id,
      itemID: item.id,
      marketplaceAccountID: account.id,
      marketplace: draft.marketplace,
      title: draft.title,
      description: draft.description,
      price: draft.price,
      shippingPrice: draft.shippingPrice,
      categoryExternalID: draft.categoryExternalID,
      conditionExternalValue: draft.conditionExternalValue,
      status: draft.validationWarnings.length > 0 ? "draft" : "ready",
      views: 0,
      watchers: 0,
      likes: 0,
      validationWarnings: draft.validationWarnings,
      createdAt: timestamp,
      updatedAt: timestamp
    };
  });

  return {
    listings,
    database: {
      ...database,
      inventoryItems: database.inventoryItems.map((candidate) =>
        candidate.id === item.id
          ? { ...candidate, status: candidate.status === "draft" ? "ready_to_list" : candidate.status,
              updatedAt: new Date(Math.max(now.getTime(), new Date(candidate.updatedAt).getTime() + 1)).toISOString() }
          : candidate
      ),
      marketplaceAccounts: [...database.marketplaceAccounts, ...fallbackAccounts],
      marketplaceListings: [...database.marketplaceListings, ...listings]
    }
  };
}

export function updateListingDraftInDatabase(
  database: ReOSDatabase,
  listingID: string,
  update: ListingDraftUpdate,
  now = new Date()
): { database: ReOSDatabase; listing: MarketplaceListing } {
  const workspace = activeWorkspace(database);
  if (!workspace) {
    throw new Error("Create or load a workspace first.");
  }
  const listing = database.marketplaceListings.find(
    (candidate) => candidate.workspaceID === workspace.id && candidate.id === listingID
  );
  if (!listing) {
    throw new Error("Listing does not exist in this workspace.");
  }
  if (!editableListingStatuses.has(listing.status)) {
    throw new Error("Only draft listings can be edited.");
  }
  if (!hasOwnUpdate(update, listingDraftUpdateKeys)) {
    throw new Error("Listing draft update is empty.");
  }

  const title = requiredUpdatedText(update, "title", listing.title, "Listing title is required.");
  const description = requiredUpdatedText(update, "description", listing.description, "Listing description is required.");
  const priceMinor = Object.prototype.hasOwnProperty.call(update, "priceMinor")
    ? positiveMinorAmount(update.priceMinor, "Listing price")
    : listing.price.amountMinor;
  const categoryExternalID = Object.prototype.hasOwnProperty.call(update, "categoryExternalID")
    ? update.categoryExternalID?.trim() || undefined
    : listing.categoryExternalID;
  if (
    (listing.marketplace === "ebay" || listing.marketplace === "etsy") &&
    Object.prototype.hasOwnProperty.call(update, "categoryExternalID") &&
    categoryExternalID &&
    !/^\d+$/.test(categoryExternalID)
  ) {
    throw new Error(`${listing.marketplace === "etsy" ? "Etsy taxonomy" : "eBay category"} ID must contain numbers only.`);
  }
  const conditionExternalValue = Object.prototype.hasOwnProperty.call(update, "conditionExternalValue")
    ? update.conditionExternalValue?.trim() || undefined
    : listing.conditionExternalValue;
  const etsyWhoMade = Object.prototype.hasOwnProperty.call(update, "etsyWhoMade")
    ? update.etsyWhoMade
    : listing.etsyWhoMade;
  const etsyWhenMade = Object.prototype.hasOwnProperty.call(update, "etsyWhenMade")
    ? update.etsyWhenMade?.trim() || undefined
    : listing.etsyWhenMade;
  const etsyIsSupply = Object.prototype.hasOwnProperty.call(update, "etsyIsSupply")
    ? update.etsyIsSupply
    : listing.etsyIsSupply;
  if (listing.marketplace === "etsy" && etsyWhoMade && !["i_did", "collective", "someone_else"].includes(etsyWhoMade)) {
    throw new Error("Choose who made this Etsy item.");
  }
  const updatedListing: MarketplaceListing = {
    ...listing,
    title,
    description,
    price: money(priceMinor, listing.price.currency),
    categoryExternalID,
    conditionExternalValue,
    etsyWhoMade,
    etsyWhenMade,
    etsyIsSupply,
    updatedAt: now.toISOString()
  };

  return {
    listing: updatedListing,
    database: {
      ...database,
      marketplaceListings: database.marketplaceListings.map((candidate) =>
        candidate.id === updatedListing.id ? updatedListing : candidate
      )
    }
  };
}

export function markListingLiveInDatabase(database: ReOSDatabase, listingID: string, externalURL?: string): ReOSDatabase {
  const now = new Date().toISOString();
  const listing = database.marketplaceListings.find((candidate) => candidate.id === listingID);
  if (!listing) {
    return database;
  }
  const liveURL = liveListingURLFor(listing, externalURL);

  return {
    ...database,
    marketplaceListings: database.marketplaceListings.map((candidate) =>
      candidate.id === listingID
        ? {
            ...candidate,
            status: "live",
            externalURL: liveURL,
            listedAt: candidate.listedAt ?? now,
            updatedAt: now
          }
        : candidate
    ),
    inventoryItems: database.inventoryItems.map((item) =>
      item.id === listing.itemID ? { ...item, status: "listed", listedAt: item.listedAt ?? now, updatedAt: now } : item
    )
  };
}

function liveListingURLFor(listing: MarketplaceListing, externalURL?: string) {
  const liveURL = externalURL?.trim() || listing.externalURL?.trim() || "";
  if (!liveURL) {
    throw new Error(LIVE_LISTING_URL_REQUIRED);
  }
  const validationError = marketplaceListingURLValidationError(listing.marketplace, liveURL);
  if (validationError) throw new Error(validationError);
  return liveURL;
}

export function marketplaceListingURLValidationError(marketplace: Marketplace, value: string) {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:") return LIVE_LISTING_URL_INVALID;
    const hostnamePattern = marketplaceListingHostnamePatterns[marketplace];
    if (hostnamePattern && !hostnamePattern.test(url.hostname)) {
      return `Paste the ${marketplaceLabels[marketplace]} listing URL, not a link from another site.`;
    }
    return undefined;
  } catch {
    return LIVE_LISTING_URL_INVALID;
  }
}

export function archiveInventoryItemInDatabase(database: ReOSDatabase, itemID: string): ReOSDatabase {
  const workspace = activeWorkspace(database);
  if (!workspace) {
    throw new Error("Create or load a workspace first.");
  }

  const item = database.inventoryItems.find((candidate) => candidate.workspaceID === workspace.id && candidate.id === itemID);
  if (!item) {
    throw new Error("Item does not exist in this workspace.");
  }
  if (item.status === "sold") {
    throw new Error("Sold items stay in sales history and cannot be archived.");
  }
  if (item.status === "archived") {
    return database;
  }

  const now = new Date().toISOString();
  const preservedListingStatuses = new Set(["sold", "ended", "delisted", "archived"]);

  return {
    ...database,
    inventoryItems: database.inventoryItems.map((candidate) =>
      candidate.workspaceID === workspace.id && candidate.id === itemID
        ? {
            ...candidate,
            status: "archived",
            updatedAt: now
          }
        : candidate
    ),
    marketplaceListings: database.marketplaceListings.map((listing) =>
      listing.workspaceID === workspace.id && listing.itemID === itemID && !preservedListingStatuses.has(listing.status)
        ? {
            ...listing,
            status: "archived",
            endedAt: listing.endedAt ?? now,
            updatedAt: now
          }
        : listing
    )
  };
}

export function removeItemPhotoFromDatabase(database: ReOSDatabase, photoID: string): ReOSDatabase {
  const workspace = activeWorkspace(database);
  if (!workspace) {
    throw new Error("Create or load a workspace first.");
  }

  const photo = database.itemPhotos.find((candidate) => candidate.id === photoID);
  const item = photo ? database.inventoryItems.find((candidate) => candidate.workspaceID === workspace.id && candidate.id === photo.itemID) : undefined;
  if (!photo || !item) {
    throw new Error("Photo does not exist in this workspace.");
  }

  const now = new Date().toISOString();
  const remainingItemPhotos = database.itemPhotos
    .filter((candidate) => candidate.itemID === item.id && candidate.id !== photoID)
    .sort((left, right) => left.sortOrder - right.sortOrder);
  const itemPhotos = database.itemPhotos.filter((candidate) => candidate.id !== photoID);
  const assetStillReferenced = photo.assetID ? itemPhotos.some((candidate) => candidate.assetID === photo.assetID) : false;

  return {
    ...database,
    inventoryItems: database.inventoryItems.map((candidate) => (candidate.id === item.id ? { ...candidate, updatedAt: now } : candidate)),
    itemPhotos,
    storedAssets:
      photo.assetID && !assetStillReferenced
        ? database.storedAssets.map((asset) =>
            asset.workspaceID === workspace.id && asset.id === photo.assetID
              ? { ...asset, itemID: undefined, cleanupPendingAt: now, updatedAt: now }
              : asset
          )
        : database.storedAssets,
    marketplaceListings: database.marketplaceListings.map((listing) => {
      if (listing.workspaceID !== workspace.id || listing.itemID !== item.id || !["draft", "ready", "failed"].includes(listing.status)) {
        return listing;
      }

      const hasPhotos = remainingItemPhotos.length > 0;
      const warnings = hasPhotos
        ? listing.validationWarnings.filter((warning) => !isNoProductPhotoWarning(warning))
        : [...new Set([...listing.validationWarnings, NO_PRODUCT_PHOTO_WARNING])];

      return {
        ...listing,
        status: hasPhotos && listing.status === "failed" && warnings.length === 0 ? "ready" : !hasPhotos ? "draft" : listing.status,
        validationWarnings: warnings,
        updatedAt: now
      };
    })
  };
}

export function addItemPhotoToDatabase(database: ReOSDatabase, itemID: string, input: PhotoInput): { database: ReOSDatabase; photo: ItemPhoto } {
  const workspace = activeWorkspace(database);
  const user = currentUser(database);
  if (!workspace || !user) {
    throw new Error("Create or load a workspace first.");
  }

  const item = database.inventoryItems.find((candidate) => candidate.workspaceID === workspace.id && candidate.id === itemID);
  if (!item) {
    throw new Error("Item does not exist in this workspace.");
  }

  const assetID = input.assetID ?? input.storedAsset?.id;
  if (!assetID) {
    throw new Error("Photo must be uploaded before it can be added to an item.");
  }

  const existingAsset = database.storedAssets.find((asset) => asset.workspaceID === workspace.id && asset.id === assetID);
  const inputAsset = input.storedAsset && input.storedAsset.workspaceID === workspace.id ? input.storedAsset : undefined;
  const asset = existingAsset ?? inputAsset;
  if (!asset) {
    throw new Error("Photo asset does not exist in this workspace.");
  }
  if (asset.cleanupPendingAt) {
    throw new Error("Photo asset is pending deletion.");
  }
  if (asset.replacementForPhotoID) {
    throw new Error("Photo asset is reserved for another photo.");
  }
  if (asset.itemID && asset.itemID !== item.id) {
    throw new Error("Photo asset is already attached to another item.");
  }
  const existingPhoto = database.itemPhotos.find(
    (photo) => photo.itemID === item.id && photo.assetID === asset.id
  );
  if (existingPhoto) {
    return {
      database,
      photo: existingPhoto
    };
  }

  const now = new Date().toISOString();
  const currentItemPhotos = database.itemPhotos.filter((photo) => photo.itemID === item.id);
  const itemPhotoCount = currentItemPhotos.length;
  if (itemPhotoCount >= MAX_ITEM_PHOTOS) {
    throw new Error(`Each item can have up to ${MAX_ITEM_PHOTOS} photos.`);
  }
  const nextSortOrder = currentItemPhotos.reduce(
    (highestSortOrder, candidate) => Math.max(highestSortOrder, candidate.sortOrder),
    -1
  ) + 1;
  const photo: ItemPhoto = {
    id: makeID("photo"),
    itemID: item.id,
    assetID: asset.id,
    url: input.url,
    thumbnailURL: input.thumbnailURL,
    sortOrder: nextSortOrder,
    type: input.type,
    width: input.width,
    height: input.height,
    treatment: input.treatment,
    createdAt: now
  };
  const linkedAsset: StoredAsset = {
    ...asset,
    itemID: item.id,
    cleanupPendingAt: undefined,
    updatedAt: now
  };

  return {
    photo,
    database: {
      ...database,
      inventoryItems: database.inventoryItems.map((candidate) => (candidate.id === item.id ? { ...candidate, updatedAt: now } : candidate)),
      itemPhotos: [...database.itemPhotos, photo],
      storedAssets: [...database.storedAssets.filter((candidate) => !(candidate.workspaceID === workspace.id && candidate.id === linkedAsset.id)), linkedAsset],
      marketplaceListings: database.marketplaceListings.map((listing) => {
        if (listing.workspaceID !== workspace.id || listing.itemID !== item.id || !["draft", "ready", "failed"].includes(listing.status)) {
          return listing;
        }

        const warnings = listing.validationWarnings.filter((warning) => !isNoProductPhotoWarning(warning));
        return {
          ...listing,
          status: warnings.length === 0 && ["draft", "failed"].includes(listing.status) ? "ready" : listing.status,
          validationWarnings: warnings,
          updatedAt: now
        };
      })
    }
  };
}

export function addItemPhotosToDatabase(
  database: ReOSDatabase,
  itemID: string,
  inputs: PhotoInput[]
): { database: ReOSDatabase; photos: ItemPhoto[] } {
  if (inputs.length === 0) {
    throw new Error("Add at least one photo.");
  }

  let nextDatabase = database;
  const photos: ItemPhoto[] = [];
  for (const input of inputs) {
    const result = addItemPhotoToDatabase(nextDatabase, itemID, input);
    nextDatabase = result.database;
    photos.push(result.photo);
  }
  return {
    database: nextDatabase,
    photos
  };
}

export function updateItemPhotoInDatabase(
  database: ReOSDatabase,
  photoID: string,
  input: PhotoInput
): { database: ReOSDatabase; photo: ItemPhoto; replacedAssetID?: string } {
  const workspace = activeWorkspace(database);
  const user = currentUser(database);
  if (!workspace || !user) {
    throw new Error("Create or load a workspace first.");
  }

  const existingPhoto = database.itemPhotos.find((candidate) => candidate.id === photoID);
  const item = existingPhoto
    ? database.inventoryItems.find((candidate) => candidate.workspaceID === workspace.id && candidate.id === existingPhoto.itemID)
    : undefined;
  if (!existingPhoto || !item) {
    throw new Error("Photo does not exist in this workspace.");
  }

  const assetID = input.assetID ?? input.storedAsset?.id;
  if (!assetID) {
    throw new Error("Photo must be uploaded before it can be updated.");
  }

  const existingAsset = database.storedAssets.find((asset) => asset.workspaceID === workspace.id && asset.id === assetID);
  const inputAsset = input.storedAsset && input.storedAsset.workspaceID === workspace.id ? input.storedAsset : undefined;
  const asset = existingAsset ?? inputAsset;
  if (!asset) {
    throw new Error("Photo asset does not exist in this workspace.");
  }
  if (asset.cleanupPendingAt) {
    throw new Error("Photo asset is pending deletion.");
  }
  if (asset.replacementForPhotoID && asset.replacementForPhotoID !== existingPhoto.id) {
    throw new Error("Photo asset is reserved for another photo.");
  }
  if (asset.itemID && asset.itemID !== item.id) {
    throw new Error("Photo asset is already attached to another item.");
  }

  const now = new Date().toISOString();
  const photo: ItemPhoto = {
    ...existingPhoto,
    assetID: asset.id,
    url: input.url,
    thumbnailURL: input.thumbnailURL,
    type: input.type,
    width: input.width,
    height: input.height,
    treatment: input.treatment
  };
  const linkedAsset: StoredAsset = {
    ...asset,
    itemID: item.id,
    replacementForPhotoID: undefined,
    cleanupPendingAt: undefined,
    updatedAt: now
  };
  const itemPhotos = database.itemPhotos.map((candidate) => (candidate.id === photo.id ? photo : candidate));
  const oldAssetStillReferenced = existingPhoto.assetID
    ? itemPhotos.some((candidate) => candidate.id !== photo.id && candidate.assetID === existingPhoto.assetID)
    : false;
  const replacedAssetID =
    existingPhoto.assetID && existingPhoto.assetID !== linkedAsset.id && !oldAssetStillReferenced
      ? existingPhoto.assetID
      : undefined;
  const storedAssets = [
    ...database.storedAssets
      .filter((candidate) => candidate.workspaceID !== workspace.id || candidate.id !== linkedAsset.id)
      .map((candidate) =>
        candidate.workspaceID === workspace.id && candidate.id === replacedAssetID
          ? { ...candidate, itemID: undefined, cleanupPendingAt: now, updatedAt: now }
          : candidate
      ),
    linkedAsset
  ];

  return {
    photo,
    replacedAssetID,
    database: {
      ...database,
      inventoryItems: database.inventoryItems.map((candidate) => (candidate.id === item.id ? { ...candidate, updatedAt: now } : candidate)),
      itemPhotos,
      storedAssets,
      marketplaceListings: database.marketplaceListings.map((listing) =>
        listing.workspaceID === workspace.id && listing.itemID === item.id && ["draft", "ready", "failed"].includes(listing.status)
          ? { ...listing, updatedAt: now }
          : listing
      )
    }
  };
}

function isNoProductPhotoWarning(warning: string) {
  return warning === NO_PRODUCT_PHOTO_WARNING || warning === LEGACY_NO_PRODUCT_PHOTO_WARNING;
}

export function recordSaleInDatabase(database: ReOSDatabase, input: SaleInput): { database: ReOSDatabase; sale: Sale } {
  const workspace = activeWorkspace(database);
  const user = currentUser(database);
  const item = database.inventoryItems.find((candidate) => candidate.id === input.itemID && candidate.workspaceID === workspace?.id);
  if (!workspace || !user || !item) {
    throw new Error("Select an inventory item before recording the sale.");
  }
  const addsPurchaseCost = Object.prototype.hasOwnProperty.call(input, "purchaseCostMinor");
  if ((addsPurchaseCost || input.expectedItemUpdatedAt !== undefined) && input.expectedItemUpdatedAt !== item.updatedAt) {
    throw new InventoryItemConflictError("This item changed. Review the latest item before recording the sale.");
  }
  if (addsPurchaseCost && purchaseCostIsKnown(item)) {
    throw new InventoryItemConflictError("This item already has a purchase cost. Review the latest item before recording the sale.");
  }
  const purchaseCost = addsPurchaseCost ? money(resolvePurchaseCost(input.purchaseCostMinor).amountMinor, workspace.defaultCurrency) : item.purchaseCost;
  const purchaseCostStatus = addsPurchaseCost || purchaseCostIsKnown(item) ? "known" as const : "unknown" as const;
  if (input.salePriceMinor <= 0) {
    throw new Error("Sale price must be greater than zero.");
  }
  assertNonNegativeSaleMoney(input);
  const externalOrderID = cleanExternalOrderID(input.externalOrderID);
  const sourceNotificationID = cleanOptionalText(input.sourceNotificationID);
  const sourceNotification = sourceNotificationID
    ? database.notifications.find(
        (notification) => notification.id === sourceNotificationID && notification.workspaceID === workspace.id
      )
    : undefined;
  const sourceSignal = sourceNotification?.marketplaceSignal;
  const ebayReview = sourceSignal?.marketplace === "ebay" ? sourceSignal.orderReview : undefined;
  if ([input.marketplaceFeesStatus, input.shippingCostStatus].some((value) => value !== undefined && value !== "known" && value !== "unknown")) throw new Error("Choose a valid cost status.");
  if (ebayReview && !Number.isSafeInteger(input.marketplaceFeesMinor)) throw new Error("Enter the eBay selling fees, including 0 if there were none.");
  if (input.stockAlreadyUpdated !== undefined && typeof input.stockAlreadyUpdated !== "boolean") throw new Error("Choose whether stock was already updated for this sale.");
  if (input.stockAlreadyUpdated && !ebayReview) throw new Error("Stock can only be preserved when reviewing a verified eBay order.");
  if (ebayReview && !input.stockAlreadyUpdated && item.quantity < 1) throw new Error("There is no stock to deduct. Check whether this sale is already included in your stock.");

  if (sourceNotificationID && !sourceNotification) {
    throw new Error("The marketplace order signal is no longer available. Refresh and try again.");
  }
  if (sourceSignal?.parserVersion?.startsWith("ebay-fulfillment-order-") && !ebayReview) {
    throw new Error("This eBay order changed or needs reconciliation. Open its latest review.");
  }
  if (externalOrderID && input.marketplace === "etsy" && !sourceSignal) {
    throw new Error("Etsy orders must be recorded from their verified Listwork order signal.");
  }
  if (sourceSignal) {
    if (
      sourceSignal.eventType !== "sale_detected" ||
      sourceSignal.externalOrderID !== externalOrderID ||
      sourceSignal.marketplace !== input.marketplace
    ) {
      throw new Error("The sale details do not match the verified marketplace order.");
    }
    if (
      (sourceSignal.externalOrderLineCount !== undefined && sourceSignal.externalOrderLineCount !== 1) ||
      (sourceSignal.externalOrderItemQuantity !== undefined && sourceSignal.externalOrderItemQuantity !== 1)
    ) {
      throw new Error("This marketplace order needs reconciliation before stock can be changed.");
    }
    if (
      sourceSignal.currency &&
      sourceSignal.currency.toUpperCase() !== workspace.defaultCurrency.toUpperCase()
    ) {
      throw new Error(
        `This order is in ${sourceSignal.currency.toUpperCase()}, but the workspace uses ${workspace.defaultCurrency.toUpperCase()}. Reconcile the currency before recording it.`
      );
    }
    if (sourceNotification?.itemID && sourceNotification.itemID !== item.id) {
      throw new Error("The selected item does not match the verified marketplace order.");
    }
  }

  const orderNotifications = externalOrderID
    ? database.notifications.filter(
        (notification) =>
          notification.workspaceID === workspace.id &&
          notification.marketplaceSignal?.marketplace === input.marketplace &&
          notification.marketplaceSignal.externalOrderID === externalOrderID
      )
    : [];
  if (externalOrderID && database.sales.some(
    (sale) => sale.workspaceID === workspace.id && sale.marketplace === input.marketplace && sale.externalOrderID === externalOrderID
  )) {
    throw new Error("This marketplace order has already been recorded.");
  }
  if (orderNotifications.some(
    (notification) => notification.marketplaceSignal?.marketplaceEventType === "order.canceled"
  )) {
    throw new Error("This marketplace order was canceled and cannot be recorded as a completed sale.");
  }
  if (externalOrderID && sourceSignal?.marketplace === "etsy" && !input.listingID) {
    throw new Error("Match this Etsy order to its live Etsy listing before changing stock.");
  }
  if (ebayReview && sourceNotification?.listingID && input.listingID !== sourceNotification.listingID) {
    throw new Error("Use the matched eBay listing for this order.");
  }
  if (input.listingID) {
    const listing = database.marketplaceListings.find((candidate) => candidate.id === input.listingID && candidate.workspaceID === workspace.id && candidate.itemID === item.id);
    const matchedEndedEbay = ebayReview && listing?.marketplace === "ebay" && listing.externalListingID === sourceSignal?.externalListingID && listing.status === "ended";
    if (!listing || (listing.status !== "live" && !matchedEndedEbay)) {
      throw new Error("Choose a live listing, or record this as a manual sale.");
    }
    if (sourceSignal?.marketplace && listing.marketplace !== sourceSignal.marketplace) {
      throw new Error("The selected listing is on a different marketplace from this order.");
    }
    if (
      (sourceSignal?.marketplace === "etsy" || ebayReview) &&
      sourceSignal?.externalListingID &&
      listing.externalListingID !== sourceSignal.externalListingID
    ) {
      throw new Error("The selected listing does not match the verified marketplace listing.");
    }
  }

  const now = new Date().toISOString();
  const currency = workspace.defaultCurrency;
  const netProfit = calculateNetProfit({
    salePriceMinor: input.salePriceMinor,
    shippingChargedMinor: input.shippingChargedMinor,
    purchaseCostMinor: purchaseCost.amountMinor,
    marketplaceFeesMinor: input.marketplaceFeesMinor,
    processingFeesMinor: input.processingFeesMinor,
    shippingCostMinor: input.shippingCostMinor,
    packagingCostMinor: input.packagingCostMinor,
    currency
  });
  const latestLifecycleAt = (eventType: "order.shipped" | "order.delivered") =>
    orderNotifications.reduce<string | undefined>((latest, notification) => {
      const signal = notification.marketplaceSignal;
      if (signal?.marketplaceEventType !== eventType) return latest;
      return !latest || signal.occurredAt > latest ? signal.occurredAt : latest;
    }, undefined);
  const shippedAt = latestLifecycleAt("order.shipped");
  const completedAt = latestLifecycleAt("order.delivered");
  const sale: Sale = {
    id: makeID("sale"),
    workspaceID: workspace.id,
    itemID: item.id,
    listingID: input.listingID,
    externalOrderID,
    externalOrderLineItemID: sourceSignal?.externalOrderLineItemID,
    marketplace: input.marketplace,
    status: completedAt ? "completed" : shippedAt || ebayReview?.shipped ? "shipped" : ebayReview?.paid ? "paid" : "sold",
    salePrice: money(input.salePriceMinor, currency),
    shippingCharged: money(input.shippingChargedMinor, currency),
    marketplaceFees: money(input.marketplaceFeesMinor, currency),
    paymentProcessingFees: money(input.processingFeesMinor, currency),
    ...(ebayReview ? {marketplaceFeesStatus: input.marketplaceFeesStatus ?? "known", costsStatus: input.marketplaceFeesStatus === "unknown" || input.shippingCostStatus === "unknown" ? "incomplete" as const : undefined, financialSync: {status: "pending" as const}} : {}),
    shippingCost: ebayReview && input.shippingCostStatus === "unknown" ? undefined : money(input.shippingCostMinor, currency),
    packagingCost: money(input.packagingCostMinor, currency),
    purchaseCost,
    purchaseCostStatus,
    netProfit,
    soldAt: input.soldAt,
    buyerName: cleanOptionalText(input.buyerName),
    buyerAddress: cleanOptionalText(input.buyerAddress),
    buyerPostcode: cleanOptionalText(input.buyerPostcode),
    shipBy: cleanOptionalText(input.shipBy),
    shippedAt,
    completedAt,
    shippingCarrier: cleanOptionalText(input.shippingCarrier),
    shippingService: cleanOptionalText(input.shippingService),
    trackingNumber: cleanOptionalText(input.trackingNumber),
    fulfilmentNotes: cleanOptionalText(input.fulfilmentNotes),
    createdAt: now,
    updatedAt: now
  };
  return {
    sale,
    database: {
      ...database,
      sales: [sale, ...database.sales],
      inventoryItems: database.inventoryItems.map((candidate) =>
        candidate.id === item.id
          ? {
              ...candidate,
              ...(addsPurchaseCost ? { purchaseCost, purchaseCostStatus } : {}),
              quantity: input.stockAlreadyUpdated ? candidate.quantity : Math.max(0, candidate.quantity - 1),
              status: !input.stockAlreadyUpdated && candidate.quantity <= 1 ? "sold" : candidate.status,
              soldAt: !input.stockAlreadyUpdated && candidate.quantity <= 1 ? input.soldAt : candidate.soldAt,
              updatedAt: now
            }
          : candidate
      ),
      marketplaceListings: database.marketplaceListings.map((listing) =>
        listing.id === input.listingID
          ? ebayReview && (input.stockAlreadyUpdated ? item.quantity : item.quantity - 1) > 0 ? listing : { ...listing, status: "sold", endedAt: input.soldAt, updatedAt: now }
          : listing.workspaceID === workspace.id &&
              listing.itemID === item.id &&
              internalListingStatusesClosedAfterSale.has(listing.status) &&
              !listing.externalURL
            ? { ...listing, status: "archived", endedAt: input.soldAt, updatedAt: now }
            : listing
      ),
      notifications: database.notifications.map((notification) =>
        externalOrderID &&
        notification.workspaceID === workspace.id &&
        notification.marketplaceSignal?.marketplace === input.marketplace &&
        notification.marketplaceSignal.externalOrderID === externalOrderID
          ? { ...notification, readAt: notification.readAt ?? now }
          : notification
      )
    }
  };
}

export function updateSaleStatusInDatabase(database: ReOSDatabase, saleID: string, update: SaleStatus | SaleFulfilmentUpdate, now = new Date()) {
  const timestamp = now.toISOString();
  const mutation: SaleFulfilmentUpdate = typeof update === "string" ? { status: update } : update;
  const changesPostage = Object.prototype.hasOwnProperty.call(mutation, "shippingCostMinor");
  if (changesPostage && (!Number.isSafeInteger(mutation.shippingCostMinor) || mutation.shippingCostMinor! < 0)) throw new Error("Postage cost must be zero or a positive amount.");
  return {
    ...database,
    sales: database.sales.map((sale) => {
      if (sale.id !== saleID) {
        return sale;
      }

      const status = mutation.status ?? sale.status;
      const nextSale: Sale = {
        ...sale,
        ...(Object.prototype.hasOwnProperty.call(mutation, "buyerName") ? { buyerName: cleanOptionalText(mutation.buyerName) } : {}),
        ...(Object.prototype.hasOwnProperty.call(mutation, "buyerAddress") ? { buyerAddress: cleanOptionalText(mutation.buyerAddress) } : {}),
        ...(Object.prototype.hasOwnProperty.call(mutation, "buyerPostcode") ? { buyerPostcode: cleanOptionalText(mutation.buyerPostcode) } : {}),
        ...(Object.prototype.hasOwnProperty.call(mutation, "shipBy") ? { shipBy: cleanOptionalText(mutation.shipBy) } : {}),
        ...(Object.prototype.hasOwnProperty.call(mutation, "shippingCarrier") ? { shippingCarrier: cleanOptionalText(mutation.shippingCarrier) } : {}),
        ...(Object.prototype.hasOwnProperty.call(mutation, "shippingService") ? { shippingService: cleanOptionalText(mutation.shippingService) } : {}),
        ...(Object.prototype.hasOwnProperty.call(mutation, "trackingNumber") ? { trackingNumber: cleanOptionalText(mutation.trackingNumber) } : {}),
        ...(Object.prototype.hasOwnProperty.call(mutation, "fulfilmentNotes") ? { fulfilmentNotes: cleanOptionalText(mutation.fulfilmentNotes) } : {}),
        ...(Object.prototype.hasOwnProperty.call(mutation, "assigneeUserID") ? { assigneeUserID: cleanOptionalText(mutation.assigneeUserID ?? undefined) } : {}),
        status,
        packedAt: mutation.status === "packed" ? sale.packedAt ?? timestamp : sale.packedAt,
        shippedAt: mutation.status === "shipped" ? sale.shippedAt ?? timestamp : sale.shippedAt,
        completedAt: mutation.status === "completed" ? sale.completedAt ?? timestamp : sale.completedAt,
        cancelledAt: mutation.status === "cancelled" ? sale.cancelledAt ?? timestamp : sale.cancelledAt,
        refundedAt: mutation.status === "refunded" ? sale.refundedAt ?? timestamp : sale.refundedAt,
        updatedAt: timestamp
      };
      if (changesPostage) {
        const shippingCost = money(mutation.shippingCostMinor!, sale.salePrice.currency);
        nextSale.shippingCost = shippingCost;
        nextSale.netProfit = { ...sale.netProfit, amountMinor: sale.netProfit.amountMinor + (sale.shippingCost?.amountMinor ?? 0) - shippingCost.amountMinor };
        nextSale.costsStatus = sale.marketplaceFeesStatus === "unknown" || sale.financialSync?.status === "review_required" ? "incomplete" : undefined;
        if (sale.financialSync) nextSale.financialSync = { ...sale.financialSync, providerShippingMinor: undefined };
      }
      // Imported shipped orders may lack local dispatch fields. A cost edit
      // does not change fulfilment or require re-entering those fields.
      if (!changesPostage || Object.keys(mutation).some((key) => key !== "shippingCostMinor")) assertSaleFulfilmentReady(nextSale);
      return nextSale;
    })
  };
}

function cleanExternalOrderID(value: string | undefined) {
  if (value === undefined) return undefined;
  if (typeof value !== "string") throw new Error("Marketplace order ID must be valid text.");
  const cleaned = value.trim();
  if (!cleaned) return undefined;
  if (cleaned.length > 200) throw new Error("Marketplace order ID is too long.");
  return cleaned;
}

function cleanOptionalText(value: string | undefined) {
  const cleaned = value?.trim();
  return cleaned || undefined;
}

function hasOwnUpdate(update: object, keys: readonly string[]) {
  return keys.some((key) => Object.prototype.hasOwnProperty.call(update, key));
}

function requiredUpdatedText<T extends object, K extends keyof T>(update: T, key: K, current: string, error: string) {
  if (!Object.prototype.hasOwnProperty.call(update, key)) {
    return current;
  }
  const value = update[key];
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(error);
  }
  return value.trim();
}

function optionalUpdatedText<T extends object, K extends keyof T>(update: T, key: K, current: string | undefined) {
  if (!Object.prototype.hasOwnProperty.call(update, key)) {
    return current;
  }
  const value = update[key];
  if (value !== null && typeof value !== "string") {
    throw new Error("Item text fields must use valid text values.");
  }
  return typeof value === "string" ? value.trim() || undefined : undefined;
}

function positiveMinorAmount(value: number | undefined, label: string) {
  if (!Number.isSafeInteger(value) || (value ?? 0) <= 0) {
    throw new Error(`${label} must be greater than zero.`);
  }
  return value as number;
}

function assertNonNegativeSaleMoney(input: SaleInput) {
  const values: Array<[label: string, amountMinor: number]> = [
    ["Shipping charged", input.shippingChargedMinor],
    ["Marketplace fees", input.marketplaceFeesMinor],
    ["Processing fees", input.processingFeesMinor],
    ["Shipping cost", input.shippingCostMinor],
    ["Packaging", input.packagingCostMinor]
  ];
  const invalid = values.find(([, amountMinor]) => amountMinor < 0);
  if (invalid) {
    throw new Error(`${invalid[0]} cannot be negative.`);
  }
}

function assertSaleFulfilmentReady(sale: Sale) {
  if (!sale.listingID || !["packed", "shipped"].includes(sale.status)) {
    return;
  }
  if (!sale.shipBy) {
    throw new Error("Set a ship-by date before packing this order.");
  }
  if (!sale.buyerAddress?.trim()) {
    throw new Error("Add the delivery address before packing this order.");
  }
  if (!sale.buyerPostcode?.trim()) {
    throw new Error("Add the delivery postcode before packing this order.");
  }
  if (sale.status === "shipped" && !sale.shippingCarrier?.trim()) {
    throw new Error("Choose a carrier before marking the order shipped.");
  }
}

function workspaceWithSettings(workspace: Workspace, update: WorkspaceSettingsUpdate, timestamp: string): Workspace {
  const name = Object.prototype.hasOwnProperty.call(update, "name") ? update.name?.trim() : workspace.name;
  const defaultCurrency = Object.prototype.hasOwnProperty.call(update, "defaultCurrency")
    ? update.defaultCurrency?.trim().toUpperCase()
    : workspace.defaultCurrency;
  const marketplaceSignalEmail = Object.prototype.hasOwnProperty.call(update, "marketplaceSignalEmail")
    ? update.marketplaceSignalEmail?.trim().toLowerCase() || undefined
    : workspace.marketplaceSignalEmail;
  const marketplaceSignalsEnabledAt = Object.prototype.hasOwnProperty.call(update, "marketplaceSignalsEnabledAt")
    ? update.marketplaceSignalsEnabledAt || undefined
    : workspace.marketplaceSignalsEnabledAt;

  if (!name) {
    throw new Error("Workspace name is required.");
  }
  if (!defaultCurrency || !/^[A-Z]{3}$/.test(defaultCurrency)) {
    throw new Error("Choose a valid default currency.");
  }
  if (marketplaceSignalEmail && !MARKETPLACE_SIGNAL_ADDRESS_PATTERN.test(marketplaceSignalEmail)) {
    throw new Error("Create a valid Listwork forwarding address.");
  }
  if (marketplaceSignalEmail && !marketplaceSignalsEnabledAt) {
    throw new Error("Email monitoring must include an activation time.");
  }
  if (!marketplaceSignalEmail && marketplaceSignalsEnabledAt) {
    throw new Error("Email monitoring requires a forwarding address.");
  }
  if (marketplaceSignalsEnabledAt && !Number.isFinite(new Date(marketplaceSignalsEnabledAt).getTime())) {
    throw new Error("Email monitoring activation time is invalid.");
  }

  return {
    ...workspace,
    name,
    defaultCurrency,
    marketplaceSignalEmail,
    marketplaceSignalsEnabledAt,
    updatedAt: timestamp
  };
}
